<template>
	<div>
		<Teleport to="body" :disabled="!shouldTeleportToBody">
			<aside v-bind="asideAttrs" :aria-hidden="!isOpen">
				<div
					:class="
						sidebarClass({
							variant: `${props.variant}${isOpen ? 'Open' : 'Closed'}`
						})
					"
					aria-label="Menu lateral"
					class="flex h-full flex-col"
				>
					<header
						class="z-50 flex shrink-0 items-center justify-between border-b border-b-primary-light-4 px-5 pb-[18px] pt-[17px] lg:py-[22px]"
					>
						<slot name="header" />
						<button
							aria-label="Fechar menu"
							class="size-6 cursor-pointer text-white"
							@click="isOpen = false"
						>
							<IconMaterialSymbolsLightClose />
						</button>
					</header>
					<main
						ref="navMobileGeneric"
						class="flex flex-1 flex-col overflow-y-auto"
					>
						<slot name="content" />
					</main>
					<footer
						class="flex w-full shrink-0 items-center justify-between border-t border-t-primary-light-4 bg-primary px-5 py-[22px]"
					>
						<slot name="footer" />
					</footer>
				</div>
				<div
					class="absolute bottom-0 z-10 h-dvh w-screen bg-primary/40"
					:class="isOpen ? 'block' : 'hidden'"
					@click="isOpen = false"
				/>
			</aside>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import { cn } from '@/utils';
import { DependencyInjectionSymbols } from '~/core/helpers/symbols';
import { cva } from 'class-variance-authority';
import { clearBodyLocks, lock, unlock } from 'tua-body-scroll-lock';
import { computed, onMounted, ref, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const teleportMobileNavToBody = inject(
	DependencyInjectionSymbols.shortsMobileNavTeleportToBody,
	false
);
const hasMounted = ref(false);

const shouldTeleportToBody = computed(() =>
	hasMounted.value ? teleportMobileNavToBody : true
);

const props = defineProps<{
	variant: 'left' | 'right';
}>();

const isOpen = defineModel('isOpen', {
	type: Boolean,
	default: false
});

const navMobileGeneric = ref<HTMLElement | null>(null);

const sidebarClass = cva(
	'absolute top-0 z-50 h-dvh w-[319px] bg-primary transition-all duration-300',
	{
		variants: {
			variant: {
				leftClosed: 'left-[-320px]',
				rightClosed: 'right-[-319px]',
				leftOpen: 'left-0',
				rightOpen: 'right-0'
			}
		},
		defaultVariants: {
			variant: 'leftClosed'
		}
	}
);

const variantClass = computed(() =>
	props.variant === 'left' ? 'left-0' : 'right-0'
);

/**
 * Padrão Vue com `Teleport` + `inheritAttrs: false`: atributos do pai não podem ir para o `<Teleport>`,
 * então repassamos ao `<aside>`. O `class` do pai entra no mesmo `cn(...)` das classes internas (Tailwind).
 */
const asideAttrs = computed(() => {
	const { class: inheritedClass, ...rest } = attrs as Record<string, unknown>;
	return {
		...rest,
		class: cn(
			'fixed inset-y-0 flex h-dvh lg:hidden',
			shouldTeleportToBody.value ? 'z-[120]' : 'z-[60]',
			isOpen.value ? 'w-screen' : 'w-0',
			variantClass.value,
			inheritedClass as string | Record<string, boolean> | string[] | undefined
		)
	};
});

watch(isOpen, value => {
	if (value) {
		lock(navMobileGeneric.value);
		if (document.body.style.top) {
			document.body.style.removeProperty('top');
		}
	} else {
		unlock(navMobileGeneric.value);
	}
});

onBeforeUnmount(() => {
	clearBodyLocks();
});

onMounted(() => {
	hasMounted.value = true;
});
</script>
